import { extendTheme } from "@chakra-ui/react";

// Ziframed Design System Theme - Rebrand 2026
const customTheme = extendTheme({
  breakpoints: {
    sm: "600px",
    md: "991px",
    lg: "1199px",
    xl: "1200px",
  },
  colors: {
    brand: {
      50: "#fcfbff",
      100: "#f9f8fd",
      200: "#f3efff",
      300: "#e7e0f2",
      400: "#ddd4ee",
      500: "#7d23ff",
      600: "#6c1ee6",
      700: "#5e2fd9",
      800: "#4f27b3",
      900: "#14003a",
    },
    purple: {
      50: "#fcfbff",
      100: "#f9f8fd",
      200: "#f3efff",
      300: "#e7e0f2",
      400: "#bbaaf6",
      500: "#7d23ff",
      600: "#6c1ee6",
      700: "#5e2fd9",
      800: "#4f27b3",
      900: "#14003a",
    },
    blue: {
      500: "#346dff",
      600: "#2d5ee6",
      700: "#2651cc",
    },
    gray: {
      50: "#fcfbff",
      100: "#f9f8fd",
      200: "#f3f2f8",
      300: "#e7e0f2",
      400: "#ddd4ee",
      500: "#84799f",
      600: "#6b6080",
      700: "#4f4672",
      800: "#2c2150",
      900: "#14003a",
    },
    text: {
      main: "#2c2150",
      soft: "#84799f",
      light: "#a39bb8",
    },
    bg: {
      app: "#f3f2f8",
      surface: "#fcfbff",
      card: "#f9f8fd",
    },
    border: {
      soft: "#e7e0f2",
      mid: "#ddd4ee",
    },
  },
  fonts: {
    heading: "'Segoe UI', Arial, sans-serif",
    body: "'Segoe UI', Arial, sans-serif",
  },
  styles: {
    global: {
      body: {
        bg: "bg.app",
        color: "text.main",
        fontFamily: "body",
      },
      "*::placeholder": {
        color: "text.soft",
      },
    },
  },
  components: {
    Button: {
      baseStyle: {
        fontWeight: 600,
        borderRadius: "12px",
        transition: "all 0.2s",
      },
      variants: {
        solid: (props) => {
          if (props.colorScheme === 'purple') {
            return {
              bgGradient: "linear(to-r, #8b1eff, #3c68ff)",
              color: "white",
              boxShadow: "0 10px 24px rgba(88, 62, 255, 0.28)",
              _hover: {
                transform: "translateY(-2px)",
                boxShadow: "0 12px 28px rgba(88, 62, 255, 0.35)",
              },
              _active: {
                transform: "translateY(0)",
                boxShadow: "0 8px 20px rgba(88, 62, 255, 0.25)",
              },
            };
          }
          return {
            bg: "purple.500",
            color: "white",
            _hover: {
              bg: "purple.600",
              transform: "translateY(-1px)",
              boxShadow: "0 4px 12px rgba(125, 35, 255, 0.3)",
            },
            _active: {
              bg: "purple.700",
              transform: "translateY(0)",
            },
          };
        },
        gradient: {
          bgGradient: "linear(to-r, purple.500, blue.500)",
          color: "white",
          boxShadow: "0 4px 16px rgba(125, 35, 255, 0.25)",
          _hover: {
            transform: "translateY(-1px)",
            boxShadow: "0 6px 20px rgba(125, 35, 255, 0.35)",
          },
          _active: {
            transform: "translateY(0)",
          },
        },
        outline: {
          bg: "white",
          color: "purple.500",
          border: "1.5px solid",
          borderColor: "border.mid",
          _hover: {
            borderColor: "purple.500",
            transform: "translateY(-1px)",
            boxShadow: "0 2px 8px rgba(125, 35, 255, 0.15)",
          },
        },
        ghost: {
          bg: "transparent",
          color: "text.main",
          _hover: {
            bg: "bg.card",
            color: "purple.500",
          },
        },
        secondary: {
          bg: "purple.500",
          color: "white",
          _hover: {
            bg: "purple.600",
            transform: "translateY(-1px)",
          },
        },
      },
      defaultProps: {
        variant: "solid",
      },
    },
    Badge: {
      baseStyle: {
        borderRadius: "8px",
        px: 3,
        py: 1,
        fontWeight: 600,
        fontSize: "xs",
        textTransform: "uppercase",
      },
      variants: {
        solid: {
          bg: "purple.500",
          color: "white",
        },
        soft: {
          bg: "purple.100",
          color: "purple.700",
        },
        outline: {
          border: "1px solid",
          borderColor: "border.mid",
          color: "text.main",
        },
      },
    },
    Card: {
      baseStyle: {
        container: {
          bg: "bg.card",
          borderRadius: "16px",
          border: "1px solid",
          borderColor: "border.soft",
          boxShadow: "0 1px 3px rgba(44, 33, 80, 0.05)",
        },
      },
    },
    Input: {
      variants: {
        outline: {
          field: {
            borderColor: "border.mid",
            bg: "white",
            _hover: {
              borderColor: "border.soft",
            },
            _focus: {
              borderColor: "purple.500",
              boxShadow: "0 0 0 1px var(--chakra-colors-purple-500)",
            },
          },
        },
      },
      defaultProps: {
        variant: "outline",
      },
    },
    Textarea: {
      variants: {
        outline: {
          borderColor: "border.mid",
          bg: "white",
          _hover: {
            borderColor: "border.soft",
          },
          _focus: {
            borderColor: "purple.500",
            boxShadow: "0 0 0 1px var(--chakra-colors-purple-500)",
          },
        },
      },
      defaultProps: {
        variant: "outline",
      },
    },
  },
});

export default customTheme;